/**
 * src/lib/statsModel.js — hàm thuần cho đo lường: làm sạch sự kiện, nhận diện
 * nguồn truy cập, và tổng hợp nhật ký thành số liệu cho /admin/thong-ke.
 *
 * Không import Firebase hay React, để test chạy bằng Node và để quy tắc làm
 * sạch dùng chung một chỗ cho cả GA4, Clarity và nhật ký nội bộ.
 */

/** Sự kiện hợp lệ. Tên lạ bị bỏ qua. Giữ khớp với firestore.rules (collection su_kien). */
export const SU_KIEN = [
  'page_view',
  'tool_start',
  'prep_save',
  'tool_export',
  'template_download',
  'playbook_read',
  'outbound_click',
  'funding_source_view',
  'funding_source_save',
  'funding_filter_use',
  'funding_cta_click',
  'funding_similar_click',
  'next_step_click',
  'return_visit_resume',
  'login_complete',
  'login_prompt_shown',
  'xem_nhan_tram',
  'bam_tram_tiep_theo',
  'mo_tram',
  'bam_viec',
  'chon_nganh',
  'xem_goi_y_tram',
  'chon_cach_dinh_gia',
  'deck_review_submit',
  'spotlight_connect_click',
  'video_play',
  // Chỉ vào nhật ký nội bộ: tab bị ẩn/đóng, để tính được thời lượng thật của
  // phiên một trang (trước đây luôn ra 0 giây). Không gửi GA4/Clarity.
  'roi_trang',
];

/**
 * Sự kiện đáng ghi vào nhật ký nội bộ (Firestore). Bộ lọc là thao tác vặt, còn
 * xem_nhan_tram đi kèm gần như mọi lượt xem trang — chỉ gửi GA4.
 */
export const SU_KIEN_NHAT_KY = new Set(SU_KIEN.filter((s) => !['funding_filter_use', 'funding_similar_click', 'login_prompt_shown', 'xem_nhan_tram'].includes(s)));

/** Nhãn tiếng Việt để hiển thị trên dashboard. */
export const NHAN_SU_KIEN = {
  page_view: 'Xem trang',
  tool_start: 'Bắt đầu dùng công cụ',
  prep_save: 'Lưu kết quả công cụ',
  tool_export: 'Xuất Excel/PDF từ công cụ',
  template_download: 'Tải mẫu',
  playbook_read: 'Đọc hết bài Playbook',
  outbound_click: 'Bấm liên kết ra ngoài',
  funding_source_view: 'Xem chi tiết nguồn vốn',
  funding_source_save: 'Lưu nguồn vốn',
  funding_filter_use: 'Dùng bộ lọc nguồn vốn',
  funding_cta_click: 'Bấm liên hệ/nộp hồ sơ quỹ',
  next_step_click: 'Bấm bước tiếp theo',
  return_visit_resume: 'Quay lại Bản chuẩn bị',
  login_complete: 'Đăng nhập',
  login_prompt_shown: 'Hiện gợi ý đăng nhập',
  xem_nhan_tram: 'Thấy nhãn trạm lộ trình',
  bam_tram_tiep_theo: 'Bấm sang trạm tiếp theo',
  mo_tram: 'Mở một trạm trên lộ trình',
  bam_viec: 'Bấm vào một việc trong trạm',
  chon_nganh: 'Chọn ngành ở Trạm 2',
  xem_goi_y_tram: 'Nhận gợi ý trạm bắt đầu',
  chon_cach_dinh_gia: 'Chọn cách định giá theo giai đoạn',
  deck_review_submit: 'Gửi deck nhận xét',
  spotlight_connect_click: 'Bấm yêu cầu kết nối startup Spotlight',
  video_play: 'Bấm xem video ngắn',
  roi_trang: 'Rời trang hoặc chuyển tab',
};

export const THAM_SO = ['tool', 'template', 'fund_id', 'filter', 'value', 'kind', 'target', 'method', 'article', 'tram', 'trang', 'tu_tram', 'den_tram', 'viec', 'dich', 'video'];

/** Chỉ giữ tham số trong danh sách; chuỗi cắt 60 ký tự, số nhỏ. Không bao giờ nhận số liệu người dùng nhập. */
export function lamSachThamSo(params = {}) {
  const out = {};
  for (const [k, v] of Object.entries(params || {})) {
    if (!THAM_SO.includes(k)) continue;
    if (typeof v === 'string') out[k] = v.slice(0, 60);
    else if (typeof v === 'number' && Number.isFinite(v) && Math.abs(v) < 1000) out[k] = v;
  }
  return out;
}

/** Đường dẫn trang: bỏ query và #, cắt 120 ký tự. Query có thể chứa dữ liệu người dùng. */
export function lamSachTrang(path) {
  const p = String(path || '/').split(/[?#]/)[0].slice(0, 120);
  return p.startsWith('/') ? p : '/' + p;
}

/** Một chuỗi ngắn tóm tắt tham số để hiện trong hành trình: "runway", "vi · mo-hinh-…". */
export function tomTatThamSo(p = {}) {
  const chinh = p.video || p.tool || p.template || p.fund_id || p.article || p.target || p.filter || p.method
    || (p.den_tram && `${p.tu_tram || '?'} → ${p.den_tram}`) || p.tram || '';
  const phu = p.kind || p.trang || '';
  return [chinh, phu].filter(Boolean).join(' · ').slice(0, 60);
}

const MIEN_TIM_KIEM = [['google.', 'google'], ['bing.', 'bing'], ['coccoc.', 'coccoc'], ['duckduckgo.', 'duckduckgo'], ['yahoo.', 'yahoo']];
// Trợ lý AI đứng trước tìm kiếm: gemini.google.com chứa "google.".
const MIEN_AI = [['chatgpt.', 'chatgpt'], ['openai.', 'chatgpt'], ['perplexity.', 'perplexity'], ['gemini.google', 'gemini'], ['bard.google', 'gemini'], ['copilot.microsoft', 'copilot'], ['copilot.', 'copilot'], ['claude.ai', 'claude'], ['deepseek.', 'deepseek'], ['grok.', 'grok'], ['you.com', 'you']];
const MIEN_XA_HOI = [['facebook.', 'facebook'], ['fb.', 'facebook'], ['messenger.', 'facebook'], ['zalo.', 'zalo'], ['linkedin.', 'linkedin'], ['lnkd.', 'linkedin'], ['t.co', 'x'], ['twitter.', 'x'], ['x.com', 'x'], ['youtube.', 'youtube'], ['tiktok.', 'tiktok'], ['threads.', 'threads']];

/**
 * Nguồn truy cập của phiên: utm_source nếu có, nếu không thì theo referrer.
 * Trả về chuỗi ngắn: 'google', 'facebook', 'truc-tiep', hoặc tên miền.
 */
export function nhanDienNguon(referrer = '', search = '', host = '') {
  const utm = /[?&]utm_source=([^&#]+)/i.exec(String(search || ''));
  if (utm) {
    let v = '';
    try { v = decodeURIComponent(utm[1]).toLowerCase().replace(/[^a-z0-9._-]/g, '').slice(0, 40); } catch { return 'utm'; }
    const ai = MIEN_AI.find(([mau]) => v.includes(mau) || v === mau.replace(/\.$/, ''));
    return ai ? ai[1] : (v || 'utm');
  }
  let h = '';
  try { h = new URL(referrer).hostname.toLowerCase().replace(/^www\.|^m\.|^l\.|^lm\./, ''); } catch { h = ''; }
  if (!h || (host && h === String(host).replace(/^www\./, ''))) return 'truc-tiep';
  for (const [mau, ten] of [...MIEN_AI, ...MIEN_TIM_KIEM, ...MIEN_XA_HOI]) if (h.includes(mau)) return ten;
  return h.slice(0, 40);
}

export const loaiNguon = (n) => {
  if (n === 'truc-tiep') return 'Trực tiếp';
  if (MIEN_AI.some(([, t]) => t === n)) return 'Trợ lý AI';
  if (MIEN_TIM_KIEM.some(([, t]) => t === n)) return 'Tìm kiếm';
  if (MIEN_XA_HOI.some(([, t]) => t === n)) return 'Mạng xã hội';
  return 'Giới thiệu/khác';
};

/** Bản ghi gửi lên Firestore. Trả null nếu không hợp lệ. Giữ khớp với firestore.rules. */
export function taoBanGhi({ ten, trang, phien, params, nguon, thietBi }) {
  if (!SU_KIEN_NHAT_KY.has(ten)) return null;
  if (!/^[a-z0-9]{8,20}$/.test(String(phien || ''))) return null;
  const rec = {
    ten,
    trang: lamSachTrang(trang),
    phien: String(phien),
    chiTiet: tomTatThamSo(lamSachThamSo(params)),
    thietBi: thietBi === 'mobile' ? 'mobile' : 'desktop',
  };
  if (nguon) rec.nguon = String(nguon).slice(0, 40);
  return rec;
}

/* ─── Tổng hợp cho dashboard ───────────────────────────────────────────── */

const ms = (v) => {
  if (!v) return 0;
  if (typeof v === 'number') return v;
  if (typeof v?.toMillis === 'function') return v.toMillis();
  if (v instanceof Date) return v.getTime();
  const t = Date.parse(v); return Number.isFinite(t) ? t : 0;
};

/** Gom trang về nhóm để đọc phễu: /cong-cu/runway → công cụ. */
export function nhomTrang(path) {
  const p = lamSachTrang(path);
  if (p === '/') return 'Trang chủ';
  if (p.startsWith('/cong-cu/')) return 'Công cụ';
  if (p === '/cong-cu') return 'Danh sách công cụ';
  if (p.startsWith('/playbook')) return 'Playbook';
  if (p.startsWith('/mau')) return 'Mẫu Excel';
  if (p.startsWith('/nguon-von') || p.startsWith('/chuong-trinh')) return 'Nguồn vốn';
  if (p.startsWith('/thuat-ngu')) return 'Từ điển';
  if (p.startsWith('/ban-chuan-bi')) return 'Bản chuẩn bị';
  if (p.startsWith('/huong-dan')) return 'Hướng dẫn';
  return 'Khác';
}

const dem = (arr, key) => {
  const m = new Map();
  for (const x of arr) { const k = key(x); if (k == null || k === '') continue; m.set(k, (m.get(k) || 0) + 1); }
  return [...m.entries()].map(([ten, so]) => ({ ten, so })).sort((a, b) => b.so - a.so || String(a.ten).localeCompare(String(b.ten)));
};

/**
 * Phiên "thoát ngay": một trang, không tương tác, và ở lại dưới ngưỡng này
 * (giây). Phiên ghi trước khi có sự kiện roi_trang luôn có thời lượng 0 nên
 * vẫn bị tính là thoát ngay.
 */
export const NGUONG_THOAT_NGAY = 10;

const SU_KIEN_TUONG_TAC = new Set(['tool_start', 'prep_save', 'tool_export', 'template_download', 'playbook_read', 'funding_source_save', 'funding_cta_click', 'next_step_click', 'login_complete']);
const SU_KIEN_HOAN_THANH = new Set(['prep_save', 'tool_export', 'template_download', 'funding_cta_click', 'funding_source_save']);

function ngayVNKey(t) {
  // Nhóm theo ngày giờ Việt Nam (UTC+7) để khớp cảm nhận của người đọc.
  return new Date(t + 7 * 3600e3).toISOString().slice(0, 10);
}

const trungVi = (a) => { const b = [...a].sort((x, y) => x - y); return b.length ? b[Math.floor(b.length / 2)] : 0; };

/**
 * Chất lượng phiên theo nhóm nguồn (Trợ lý AI, Tìm kiếm...): để so trước và sau
 * khi sửa một trang, ví dụ khách từ ChatGPT vào /nguon-von có ở lại lâu hơn không.
 */
export function chatLuongTheoNguon(phien = []) {
  const nhom = new Map();
  for (const p of phien) {
    const k = loaiNguon(p.nguon);
    if (!nhom.has(k)) nhom.set(k, []);
    nhom.get(k).push(p);
  }
  return [...nhom.entries()].map(([loai, ds]) => {
    const vaoNV = ds.filter((p) => String(p.trangVao || '').startsWith('/nguon-von'));
    return {
      loai,
      so: ds.length,
      thoiLuongTrungVi: trungVi(ds.map((p) => p.thoiLuong)),
      tyLeThoatNgay: ds.filter((p) => p.soTrang <= 1 && !p.coTuongTac && p.thoiLuong < NGUONG_THOAT_NGAY).length / ds.length,
      trangMoiPhien: ds.reduce((t, p) => t + p.soTrang, 0) / ds.length,
      vaoNguonVon: vaoNV.length,
      thoiLuongNguonVon: trungVi(vaoNV.map((p) => p.thoiLuong)),
    };
  }).sort((a, b) => b.so - a.so);
}

/**
 * Nhận danh sách bản ghi { ten, trang, phien, chiTiet, nguon, thietBi, createdAt }
 * (thứ tự bất kỳ) và trả về toàn bộ số liệu cho dashboard.
 */
export function tongHop(records = [], { soNgay = 7, bayGio = Date.now() } = {}) {
  const ev = records.map((r) => ({ ...r, t: ms(r.createdAt) })).filter((r) => r.t && r.phien && r.ten)
    .sort((a, b) => a.t - b.t);

  const theoPhien = new Map();
  for (const e of ev) {
    if (!theoPhien.has(e.phien)) theoPhien.set(e.phien, []);
    theoPhien.get(e.phien).push(e);
  }

  const phien = [...theoPhien.entries()].map(([id, list]) => {
    const trang = list.filter((e) => e.ten === 'page_view').map((e) => e.trang);
    // Gộp các lần xem liên tiếp cùng một trang (tải lại, quay lại nhanh).
    const duong = trang.filter((p, i) => p !== trang[i - 1]);
    const tuongTac = list.filter((e) => SU_KIEN_TUONG_TAC.has(e.ten));
    return {
      id,
      batDau: list[0].t,
      ketThuc: list[list.length - 1].t,
      thoiLuong: Math.round((list[list.length - 1].t - list[0].t) / 1000),
      thietBi: list[0].thietBi || 'desktop',
      nguon: list.find((e) => e.nguon)?.nguon || 'truc-tiep',
      trangVao: duong[0] || list[0].trang,
      trangThoat: duong[duong.length - 1] || list[list.length - 1].trang,
      soTrang: trang.length,
      duong,
      coTuongTac: tuongTac.length > 0,
      hoanThanh: list.some((e) => SU_KIEN_HOAN_THANH.has(e.ten)),
      buoc: list.map((e) => ({ t: e.t, ten: e.ten, trang: e.trang, chiTiet: e.chiTiet || '' })),
    };
  }).sort((a, b) => b.batDau - a.batDau);

  const soPhien = phien.length;
  const pv = ev.filter((e) => e.ten === 'page_view');
  const tyLe = (a, b) => (b ? a / b : 0);

  // Theo ngày: đủ số ngày kể cả ngày trống, để biểu đồ không bị hở.
  const theoNgayMap = new Map();
  for (let i = soNgay - 1; i >= 0; i--) theoNgayMap.set(ngayVNKey(bayGio - i * 864e5), { ngay: ngayVNKey(bayGio - i * 864e5), phien: 0, luotXem: 0 });
  for (const p of phien) { const k = ngayVNKey(p.batDau); if (theoNgayMap.has(k)) theoNgayMap.get(k).phien += 1; }
  for (const e of pv) { const k = ngayVNKey(e.t); if (theoNgayMap.has(k)) theoNgayMap.get(k).luotXem += 1; }

  // Phễu theo phiên.
  const coNhom = (p, nhom) => p.duong.some((x) => nhom.includes(nhomTrang(x)));
  const pheu = [
    { buoc: 'Vào website', so: soPhien },
    { buoc: 'Mở công cụ, Playbook hoặc mẫu', so: phien.filter((p) => coNhom(p, ['Công cụ', 'Playbook', 'Mẫu Excel'])).length },
    { buoc: 'Có tương tác thật', so: phien.filter((p) => p.coTuongTac).length },
    { buoc: 'Hoàn thành (lưu, xuất, tải mẫu, liên hệ quỹ)', so: phien.filter((p) => p.hoanThanh).length },
  ];

  // Công cụ: bắt đầu → lưu → xuất.
  const congCu = new Map();
  for (const e of ev) {
    if (!['tool_start', 'prep_save', 'tool_export'].includes(e.ten)) continue;
    const ten = String(e.chiTiet || '').split(' · ')[0] || '?';
    if (!congCu.has(ten)) congCu.set(ten, { ten, batDau: 0, luu: 0, xuat: 0 });
    const c = congCu.get(ten);
    if (e.ten === 'tool_start') c.batDau += 1; else if (e.ten === 'prep_save') c.luu += 1; else c.xuat += 1;
  }

  const chuoiDuong = (p) => p.duong.slice(0, 4).join(' → ') + (p.duong.length > 4 ? ' → …' : '');

  return {
    soPhien,
    luotXem: pv.length,
    trangMoiPhien: tyLe(pv.length, soPhien),
    tyLeTuongTac: tyLe(phien.filter((p) => p.coTuongTac).length, soPhien),
    tyLeHoanThanh: tyLe(phien.filter((p) => p.hoanThanh).length, soPhien),
    tyLeThoatNgay: tyLe(phien.filter((p) => p.soTrang <= 1 && !p.coTuongTac && p.thoiLuong < NGUONG_THOAT_NGAY).length, soPhien),
    thoiLuongTrungVi: (() => { const a = phien.map((p) => p.thoiLuong).sort((x, y) => x - y); return a.length ? a[Math.floor(a.length / 2)] : 0; })(),
    theoNgay: [...theoNgayMap.values()],
    pheu,
    trangVao: dem(phien, (p) => p.trangVao).slice(0, 10),
    trangThoat: dem(phien, (p) => p.trangThoat).slice(0, 10),
    trangXem: dem(pv, (e) => e.trang).slice(0, 15),
    theoLoaiNguon: chatLuongTheoNguon(phien),
    nguon: dem(phien, (p) => p.nguon).slice(0, 10).map((x) => ({ ...x, loai: loaiNguon(x.ten) })),
    thietBi: dem(phien, (p) => (p.thietBi === 'mobile' ? 'Điện thoại' : 'Máy tính')),
    hanhTrinh: dem(phien.filter((p) => p.duong.length > 1), chuoiDuong).slice(0, 10),
    suKien: dem(ev.filter((e) => e.ten !== 'page_view' && e.ten !== 'roi_trang'), (e) => e.ten).map((x) => ({ ...x, nhan: NHAN_SU_KIEN[x.ten] || x.ten })),
    congCu: [...congCu.values()].sort((a, b) => b.batDau - a.batDau || b.luu - a.luu),
    mauTai: dem(ev.filter((e) => e.ten === 'template_download'), (e) => e.chiTiet).slice(0, 15),
    baiDocHet: dem(ev.filter((e) => e.ten === 'playbook_read'), (e) => e.chiTiet).slice(0, 15),
    lienKetNgoai: dem(ev.filter((e) => e.ten === 'outbound_click'), (e) => e.chiTiet).slice(0, 10),
    phienGanDay: phien.slice(0, 30),
  };
}
